import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isTextNode,
  SKIP_DOM_SELECTION_TAG,
  type LexicalEditor,
  type LexicalNode,
  type TextNode,
  type UpdateTag,
} from "lexical";

import {
  getTrailingSlashQueryMatch,
  hasTrailingSlashQuery,
} from "./DirectiveComposer.querying";

export const hasDirectiveInvocationNodes = (
  node: LexicalNode,
  isDirectiveNode: (node: LexicalNode) => boolean
): boolean => {
  if (isDirectiveNode(node)) return true;

  if (!$isElementNode(node)) return false;

  return node
    .getChildren()
    .some((child) => hasDirectiveInvocationNodes(child, isDirectiveNode));
};

interface SetEditorPlainTextOptions {
  onUpdate?: () => void;
  skipDomSelection?: boolean;
  tag?: UpdateTag | UpdateTag[];
}

// An empty array is safe: editor.update treats it exactly like no tag at all.
export const getEditorUpdateTags = ({
  skipDomSelection,
  tag,
}: {
  skipDomSelection?: boolean;
  tag?: UpdateTag | UpdateTag[];
}): UpdateTag[] => [
  ...(skipDomSelection ? [SKIP_DOM_SELECTION_TAG] : []),
  ...(Array.isArray(tag) ? tag : tag ? [tag] : []),
];

export const setEditorPlainText = (
  editor: LexicalEditor,
  text: string,
  options: SetEditorPlainTextOptions = {}
) => {
  editor.update(
    () => {
      const root = $getRoot();
      const paragraph = $createParagraphNode();

      if (text) {
        paragraph.append($createTextNode(text));
      }

      root.clear();
      root.append(paragraph);
      paragraph.selectEnd();
    },
    {
      discrete: true,
      onUpdate: options.onUpdate,
      tag: getEditorUpdateTags(options),
    }
  );
};

export const getLastTextNodeWithTrailingSlash = (
  node: LexicalNode
): LexicalNode | null => {
  if ($isTextNode(node) && hasTrailingSlashQuery(node.getTextContent())) {
    return node;
  }

  if (!$isElementNode(node)) return null;

  const children = node.getChildren();

  for (let index = children.length - 1; index >= 0; index -= 1) {
    const match = getLastTextNodeWithTrailingSlash(children[index]);
    if (match) return match;
  }

  return null;
};

export const insertDirectiveInvocationAtTrailingSlash = <N extends LexicalNode>(
  directiveNode: N,
  trailingSpace: TextNode
): boolean => {
  const textNode = getLastTextNodeWithTrailingSlash($getRoot());

  if (!$isTextNode(textNode)) return false;

  const text = textNode.getTextContent();
  const slashMatch = getTrailingSlashQueryMatch(text);
  if (!slashMatch) return false;

  const slashStart = text.length - slashMatch[0].length;
  textNode.setTextContent(text.slice(0, slashStart));
  const insertedDirective = textNode.insertAfter(directiveNode);
  insertedDirective.insertAfter(trailingSpace);
  trailingSpace.select(1, 1);

  return true;
};
